import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Card,
  Col,
  Empty,
  Image,
  Pagination,
  Row,
  Statistic,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import default_bank from '../../../../Assets/ic_default_bank.png';
import { ConsentDetail } from '../../../../Models/Response/Consent/GetAllConsent';
import {
  getConsentCounts,
  getPaginatedConsentByStatus,
} from '../../../../Services/ConsentServices/ConsentService';
import { logoBaseUrl } from '../../../../Utils/Constants/baseUrl';
import {
  ConsentApprovalStatusType,
  ConsentStatusType,
} from '../../../../Utils/Constants/consentTypes';
import TimeFromNow from '../../../Common/Components/TimeFromNow';
import './index.scss';

const { TabPane } = Tabs;

const ConsentComponent: React.FC = () => {
  const history = useHistory();
  const [consents, setConsents] = useState({
    numberOfPendingConsent: 0,
    pendingConsent: new Array<ConsentDetail>(),
    numberOfFailedConsent: 0,
    failedConsent: new Array<ConsentDetail>(),
    numberOfActiveConsent: 0,
    activeConsent: new Array<ConsentDetail>(),
  });
  const [pausedConsent, setPausedConsent] = useState(
    new Array<ConsentDetail>()
  );
  const [numberOfPausedConsent, setNumberOfPausedConsent] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState([ConsentStatusType.CREATED]);
  const { t } = useTranslation();

  const getConsentsByStatus = useCallback(() => {
    getPaginatedConsentByStatus(page - 1, pageSize, status.toString())
      .then((res) => res.data)
      .then((res) => {
        switch (status.toString()) {
          case ConsentStatusType.PAUSED:
            setPausedConsent(res.content);
            setNumberOfPausedConsent(res.totalElements);
            break;

          case ConsentStatusType.CREATED:
            setConsents((old) => ({
              ...old,
              pendingConsent: res.content,
              numberOfPendingConsent: res.totalElements,
            }));
            break;

          case ConsentStatusType.ACTIVE:
            setConsents((old) => ({
              ...old,
              activeConsent: res.content,
              numberOfActiveConsent: res.totalElements,
            }));
            break;

          case `${ConsentStatusType.EXPIRED},${ConsentStatusType.REJECTED},${ConsentStatusType.REVOKED}`:
            setTotalCount(res.totalElements);
            setConsents((old) => ({
              ...old,
              failedConsent: res.content,
              numberOfFailedConsent: res.totalElements,
            }));
            break;
          default:
            break;
        }
        setTotalCount(res.totalElements);
        getConsentCounts()
          .then((res1) => res1.data)
          .then((res1) => {
            setNumberOfPausedConsent(res1.numberOfPausedConsent);
            setConsents((old) => ({
              ...old,
              numberOfActiveConsent: res1.numberOfActiveConsent,
              numberOfFailedConsent: res1.numberOfOtherConsent,
              numberOfPendingConsent: res1.numberOfPendingConsent,
            }));
          });
      });
  }, [pageSize, page, status]);

  useEffect(() => {
    getConsentsByStatus();
  }, [getConsentsByStatus]);

  const cardClicked = (
    consentUniqueId: string,
    type: string,
    createdOrUpdatedOn: string
  ) => {
    history.push({
      pathname: `/consent/${type}/${consentUniqueId}`,
      state: { createdOrUpdatedOn },
    });
  };

  const returnNumberOfActiveConsents = () => {
    return (
      <>
        <Typography.Text className='fiuName'>
          {t('Active Consent')}
        </Typography.Text>{' '}
        <Badge count={consents.numberOfActiveConsent} showZero={true} />
      </>
    );
  };

  const returnNumberOfPendingConsents = () => {
    return (
      <>
        <Typography.Text className='fiuName'>
          {t('Pending Consent')}
        </Typography.Text>{' '}
        <Badge count={consents.numberOfPendingConsent} showZero={true} />
      </>
    );
  };

  const returnNumberOfPausedConsents = () => {
    return (
      <>
        <Typography.Text className='fiuName'>
          {t('Paused Consent')}
        </Typography.Text>{' '}
        <Badge count={numberOfPausedConsent} showZero={true} />
      </>
    );
  };

  const returnNumberOfOtherConsents = () => {
    return (
      <>
        <Typography.Text className='fiuName'>
          {t('Other Consents')}
        </Typography.Text>{' '}
        <Badge count={consents.numberOfFailedConsent} showZero={true} />
      </>
    );
  };

  const onTabChange = (activeKey: string) => {
    setPage(1);
    switch (activeKey) {
      case '1':
        setStatus([ConsentStatusType.ACTIVE]);
        break;
      case '2':
        setStatus([ConsentStatusType.CREATED]);
        break;
      case '3':
        setStatus([ConsentStatusType.PAUSED]);
        break;
      case '4':
        setStatus([
          ConsentStatusType.EXPIRED,
          ConsentStatusType.REJECTED,
          ConsentStatusType.REVOKED,
        ]);
        break;
      default:
        break;
    }
  };

  const showTotal = (total: number, range: number[]) => (
    <Statistic title={`${range[0]}-${range[1]} of ${total} items`} />
  );

  const onPageAndPageSizeChange = (pageRef: number, pageSizeRef: number) => {
    setPage(pageRef);
    setPageSize(pageSizeRef);
  };

  const iconRenderer = (consent: ConsentDetail) => {
    if (consent.consentStatusType === ConsentStatusType.REJECTED) {
      return <CloseCircleOutlined />;
    }

    return consent.consentStatusType === ConsentStatusType.REVOKED ? (
      <MinusCircleOutlined />
    ) : (
      <ClockCircleOutlined />
    );
  };

  const colorRenderer = (consent: ConsentDetail) => {
    if (consent.consentStatusType === ConsentStatusType.REJECTED) {
      return 'error';
    }
    return consent.consentStatusType === ConsentStatusType.REVOKED
      ? 'warning'
      : 'default';
  };

  return (
    <div className='consentLayout'>
      <Typography.Title
        level={2}
        style={{
          marginLeft: 'auto',
          color: 'var(--ant-primary-color)',
          width: '95%',
          marginRight: 'auto',
          marginTop: 15,
        }}
      >
        {t('Consent(s)')}
      </Typography.Title>
      <Pagination
        size='small'
        total={totalCount}
        defaultCurrent={6}
        current={page}
        defaultPageSize={20}
        showSizeChanger={true}
        className='pagination'
        showTotal={showTotal}
        responsive={true}
        onChange={onPageAndPageSizeChange}
      />
      <Card className='consentDetailsHome'>
        <Tabs
          onChange={onTabChange}
          animated={true}
          defaultActiveKey='2'
          className='consentTabs'
        >
          <TabPane tab={returnNumberOfActiveConsents()} key='1'>
            <Row justify='space-between'>
              {consents.activeConsent && consents.activeConsent.length ? (
                consents.activeConsent.map((consent: ConsentDetail) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 11 }}
                    lg={{ span: 11 }}
                  >
                    <Card
                      key={consent.consentHandle}
                      hoverable={true}
                      // tslint:disable-next-line: jsx-no-lambda
                      onClick={() =>
                        cardClicked(
                          consent.consentid,
                          ConsentStatusType.ACTIVE,
                          consent.updatedOn
                        )
                      }
                      className='consentCard'
                    >
                      <Image
                        className='fipLogo'
                        src={logoBaseUrl + consent.fiuId + '.png'}
                        fallback={default_bank}
                        preview={false}
                      />
                      <div className='typography'>
                        <div className='fiuName'>
                          <strong>{consent.fiuName}</strong>
                          <div>
                            <Typography.Text className='type'>
                              Purpose :{' '}
                            </Typography.Text>
                            <Typography.Text className='value' type='secondary'>
                              {consent.purpose}
                            </Typography.Text>
                          </div>
                        </div>
                        <TimeFromNow
                          date={consent.updatedOn}
                          title={t('Updated')}
                        />
                      </div>
                    </Card>
                  </Col>
                ))
              ) : (
                <Empty
                  style={{ marginTop: 10, width: '100%' }}
                  description={
                    <Typography.Text className='consentErrorMsg'>
                      {t('No')} {t('Active Consent')}
                    </Typography.Text>
                  }
                />
              )}
            </Row>
          </TabPane>
          <TabPane tab={returnNumberOfPendingConsents()} key='2'>
            <Row justify='space-between'>
              {consents.pendingConsent && consents.pendingConsent.length ? (
                consents.pendingConsent.map((consent: ConsentDetail) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 11 }}
                    lg={{ span: 11 }}
                  >
                    <Card
                      key={consent.consentHandle}
                      hoverable={true}
                      // tslint:disable-next-line: jsx-no-lambda
                      onClick={() =>
                        cardClicked(
                          consent.consentHandle,
                          ConsentApprovalStatusType.PENDING,
                          consent.createdOn
                        )
                      }
                      className='consentCard'
                    >
                      <Image
                        className='fipLogo'
                        src={logoBaseUrl + consent.fiuId + '.png'}
                        fallback={default_bank}
                        preview={false}
                      />
                      <div className='typography'>
                        <div className='fiuName'>
                          <strong>{consent.fiuName}</strong>
                          <div>
                            <Typography.Text className='type'>
                              Purpose :{' '}
                            </Typography.Text>
                            <Typography.Text className='value' type='secondary'>
                              {consent.purpose}
                            </Typography.Text>
                          </div>
                        </div>
                        <TimeFromNow date={consent.createdOn} />
                      </div>
                    </Card>
                  </Col>
                ))
              ) : (
                <Empty
                  style={{ marginTop: 10, width: '100%' }}
                  description={
                    <Typography.Text className='consentErrorMsg'>
                      {t('No')} {t('Pending Consent')}
                    </Typography.Text>
                  }
                />
              )}
            </Row>
          </TabPane>
          <TabPane tab={returnNumberOfPausedConsents()} key='3'>
            <Row justify='space-between'>
              {pausedConsent && pausedConsent.length ? (
                pausedConsent.map((consent: any) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 11 }}
                    lg={{ span: 11 }}
                  >
                    <Card
                      key={consent.consentHandle}
                      hoverable={true}
                      // tslint:disable-next-line: jsx-no-lambda
                      onClick={() =>
                        cardClicked(
                          consent.consentid,
                          ConsentStatusType.PAUSED,
                          consent.updatedOn
                        )
                      }
                      className='consentCard'
                    >
                      <Image
                        className='fipLogo'
                        src={logoBaseUrl + consent.fiuId + '.png'}
                        fallback={default_bank}
                        preview={false}
                      />
                      <div className='typography'>
                        <div className='fiuName'>
                          <strong>{consent.fiuName}</strong>
                          <div>
                            <Typography.Text className='type'>
                              Purpose :{' '}
                            </Typography.Text>
                            <Typography.Text className='value' type='secondary'>
                              {consent.purpose}
                            </Typography.Text>
                          </div>
                        </div>
                        <TimeFromNow
                          date={consent.updatedOn}
                          title={t('Updated')}
                        />
                      </div>
                    </Card>
                  </Col>
                ))
              ) : (
                <Empty
                  style={{ marginTop: 10, width: '100%' }}
                  description={
                    <Typography.Text className='consentErrorMsg'>
                      {t('No')} {t('Paused Consent')}
                    </Typography.Text>
                  }
                />
              )}
            </Row>
          </TabPane>
          <TabPane tab={returnNumberOfOtherConsents()} key='4'>
            <Row justify='space-between'>
              {consents.failedConsent && consents.failedConsent.length ? (
                consents.failedConsent.map((consent: ConsentDetail) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 11 }}
                    lg={{ span: 11 }}
                  >
                    <Card
                      key={consent.consentHandle}
                      hoverable={true}
                      // tslint:disable-next-line: jsx-no-lambda
                      onClick={() =>
                        cardClicked(
                          consent.consentStatusType ===
                            ConsentStatusType.REVOKED
                            ? consent.consentid
                            : consent.consentHandle,
                          consent.consentStatusType,
                          consent.updatedOn
                        )
                      }
                      className='consentCard'
                    >
                      <Image
                        className='fipLogo'
                        src={logoBaseUrl + consent.fiuId + '.png'}
                        fallback={default_bank}
                        preview={false}
                      />
                      <div className='typography'>
                        <div className='fiuName'>
                          <strong>{consent.fiuName}</strong>
                          <div>
                            <Typography.Text className='type'>
                              Purpose :{' '}
                            </Typography.Text>
                            <Typography.Text className='value' type='secondary'>
                              {consent.purpose}
                            </Typography.Text>
                          </div>
                        </div>
                        <div>
                          <TimeFromNow
                            date={consent.updatedOn}
                            title={t('Updated')}
                          />

                          <Tag
                            className='tag'
                            icon={iconRenderer(consent)}
                            color={colorRenderer(consent)}
                          >
                            {consent.consentStatusType}
                          </Tag>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))
              ) : (
                <Empty
                  style={{ marginTop: 10, width: '100%' }}
                  description={
                    <Typography.Text className='consentErrorMsg'>
                      {t('No')} {t('Other Consents')}
                    </Typography.Text>
                  }
                />
              )}
            </Row>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default ConsentComponent;
